/**
 * Donde queda la pagina al navegar.
 *
 * En una SPA el navegador no lo resuelve solo:
 * - Al ir a una pagina nueva, arriba: sin esto, entrar a una ficha desde la
 *   mitad de un listado deja la ficha abierta a media altura. No aplica cuando
 *   solo cambian los parametros (ordenar un listado), porque ahi la persona
 *   sigue mirando lo mismo.
 * - Al volver con el boton atras, donde estaba. Antes tambien subia arriba, y
 *   en el telefono el recorrido de siempre —bajar por el listado, abrir un
 *   producto, volver— devolvia al principio de la lista. Las paginas que ya
 *   se habian sumado las recuerda `use-listado-infinito.ts`; aca se espera a
 *   que la pagina vuelva a tener ese alto y se baja hasta la posicion.
 *
 * Las posiciones viven en memoria, por entrada del historial (`location.key`):
 * recargar la pagina las pierde, como pierde las paginas sumadas.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** Cuanto se espera, como mucho, a que la pagina recupere su alto. */
const ESPERA_MAXIMA_MS = 3000;

const posiciones = new Map<string, number>();

/** Baja hasta `objetivo` apenas la pagina lo permita; lo corta si la persona se mueve antes. */
function restaurar(objetivo: number): () => void {
  const inicio = performance.now();
  let cuadro = 0;
  let activa = true;

  const cortar = (): void => {
    activa = false;
    cancelAnimationFrame(cuadro);
    window.removeEventListener('wheel', cortar);
    window.removeEventListener('touchstart', cortar);
    window.removeEventListener('keydown', cortar);
  };

  const intentar = (): void => {
    if (!activa) return;
    const alcanzable = document.documentElement.scrollHeight - window.innerHeight;
    if (alcanzable >= objetivo || performance.now() - inicio > ESPERA_MAXIMA_MS) {
      window.scrollTo({ top: objetivo });
      cortar();
      return;
    }
    cuadro = requestAnimationFrame(intentar);
  };

  window.addEventListener('wheel', cortar, { passive: true });
  window.addEventListener('touchstart', cortar, { passive: true });
  window.addEventListener('keydown', cortar);
  intentar();
  return cortar;
}

export function VolverArriba() {
  const { pathname, key } = useLocation();
  const tipo = useNavigationType();
  const claveActual = useRef(key);
  const rutaPrevia = useRef(pathname);

  // El navegador intentaria restaurar por su cuenta, antes de que React pinte
  // la pagina, y pelearia con lo de abajo.
  useEffect(() => {
    const previo = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previo;
    };
  }, []);

  useEffect(() => {
    const guardar = (): void => {
      posiciones.set(claveActual.current, window.scrollY);
    };
    window.addEventListener('scroll', guardar, { passive: true });
    return () => window.removeEventListener('scroll', guardar);
  }, []);

  // `useLayoutEffect`: la clave tiene que cambiar antes de que el navegador
  // recorte el desplazamiento de la pagina nueva (mas corta mientras carga);
  // si no, ese recorte se guardaria como la posicion de la pagina anterior.
  useLayoutEffect(() => {
    claveActual.current = key;
    const cambioDeRuta = rutaPrevia.current !== pathname;
    rutaPrevia.current = pathname;

    if (tipo === 'POP') {
      const guardada = posiciones.get(key);
      if (guardada !== undefined) return restaurar(guardada);
    }
    if (cambioDeRuta) window.scrollTo({ top: 0 });
    return undefined;
  }, [key, pathname, tipo]);

  return null;
}
